import type { DataNode } from 'components/WorkspaceEditor/types/types'
import { tryGetShortcutKey } from 'components/WorkspaceEditor/utils/shortcutKeyUtils'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useCallback, useEffect } from 'react'
import {
    useNodeMapActions,
    useNodeMapShallowSelector,
} from '../store/NodeMapStore'
import type { SwitcherNavigationParams } from '../types'
import {
    getSwitcherIconSelector,
    isArrowDownShortcut,
    isArrowUpShortcut,
} from '../utils'

// Configuration constants for timing operations
const FOCUS_TIMING_CONFIG = {
    VIRTUALIZATION_RENDER_DELAY: 100, // Wait for virtualization to render nodes
    FOCUS_RETRY_DELAY: 50, // Delay before retrying focus operations
    FOCUS_RESTORATION_DELAY: 150, // Delay to ensure DOM updates after loading
    INITIAL_FOCUS_DELAY: 50, // Initial delay before attempting focus
} as const

/**
 * Creates a flattened list of visible nodes respecting expansion state and hidden nodes
 *
 * @param nodes - Tree nodes to flatten
 * @param expandedKeys - Currently expanded keys
 * @returns Flattened array of visible nodes
 */
const flattenVisibleNodes = (
    nodes: DataNode[],
    expandedKeys: string[]
): DataNode[] => {
    const result: DataNode[] = []

    const traverse = (nodeList: DataNode[]) => {
        for (const nodeItem of nodeList) {
            // Only check if current node is hidden, not parent
            if (!nodeItem.isHidden) {
                result.push(nodeItem)
            }
            // Always traverse children if parent is expanded, regardless of parent's hidden state
            if (
                expandedKeys.includes(nodeItem.key) &&
                nodeItem.children?.length
            ) {
                traverse(nodeItem.children)
            }
        }
    }

    traverse(nodes)
    return result
}

/**
 * Finds the next or previous focusable node in the flattened tree
 *
 * @param currentNode - Current node
 * @param direction - Navigation direction
 * @param treeData - Tree data
 * @param expandedKeys - Expanded keys
 * @returns Next/previous node or null
 */
const findNextFocusableNode = (
    currentNode: DataNode,
    direction: 'up' | 'down',
    treeData: DataNode[],
    expandedKeys: string[]
): DataNode | null => {
    const flatNodes = flattenVisibleNodes(treeData, expandedKeys)
    const currentIndex = flatNodes.findIndex((n) => n.key === currentNode.key)

    if (currentIndex === -1) return null

    if (direction === 'down') {
        return currentIndex < flatNodes.length - 1
            ? flatNodes[currentIndex + 1]
            : null
    } else {
        return currentIndex > 0 ? flatNodes[currentIndex - 1] : null
    }
}

/**
 * Focuses on the target element based on node type with optional virtualization support
 *
 * @param targetElement - HTML element to focus
 * @returns true if focus was successful, false otherwise
 */
const focusElement = (targetElement: HTMLElement): boolean => {
    if (targetElement) {
        targetElement.focus()
        return true
    }
    return false
}

/**
 * Attempts to focus on the target node with virtualization support
 * Simplified version with single DOM query attempt per node type
 *
 * @param nextNode - Node to focus on
 * @param treeRef - Tree reference for scrolling
 * @returns Promise that resolves when focus operation is complete
 */
const focusNodeElement = async (
    nextNode: DataNode,
    treeRef?: React.MutableRefObject<{
        scrollTo?: (options: {
            key: string | number
            align?: 'top' | 'bottom' | 'auto'
            offset?: number
        }) => void
    }>
): Promise<void> => {
    return new Promise((resolve) => {
        setTimeout(async () => {
            // First, ensure the target node is rendered by scrolling to it
            if (treeRef?.current?.scrollTo) {
                treeRef.current.scrollTo({
                    key: nextNode.key,
                    align: 'auto',
                })

                // Wait for virtualization to render the node
                await new Promise((resolveWait) =>
                    setTimeout(
                        resolveWait,
                        FOCUS_TIMING_CONFIG.VIRTUALIZATION_RENDER_DELAY
                    )
                )
            }

            // Attempt to focus based on node type - single attempt per type
            let focused = false
            const isArticle = nextNode.type === NODE_TYPES.Article

            if (isArticle) {
                // For articles, focus on switcher icon
                const switcherIcon = document.querySelector(
                    getSwitcherIconSelector(nextNode.key)
                ) as HTMLElement
                focused = focusElement(switcherIcon)
            } else {
                // For non-article nodes, try different elements in priority order
                const nodeElement = document.querySelector(
                    `[data-key="${nextNode.key}"]`
                ) as HTMLElement

                if (nodeElement) {
                    const focusableSpan = nodeElement.querySelector(
                        'span[tabindex="0"]'
                    ) as HTMLElement
                    const titleSpan = nodeElement.querySelector(
                        'span'
                    ) as HTMLElement
                    const targetElement =
                        focusableSpan || titleSpan || nodeElement
                    focused = focusElement(targetElement)
                }
            }

            // Single retry if initial attempt failed (for virtualization edge cases)
            if (!focused) {
                setTimeout(() => {
                    if (isArticle) {
                        const switcherIcon = document.querySelector(
                            getSwitcherIconSelector(nextNode.key)
                        ) as HTMLElement
                        focusElement(switcherIcon)
                    } else {
                        const nodeElement = document.querySelector(
                            `[data-key="${nextNode.key}"]`
                        ) as HTMLElement
                        if (nodeElement) {
                            const targetElement =
                                (nodeElement.querySelector(
                                    'span[tabindex="0"]'
                                ) as HTMLElement) ||
                                (nodeElement.querySelector(
                                    'span'
                                ) as HTMLElement) ||
                                nodeElement
                            focusElement(targetElement)
                        }
                    }
                    resolve()
                }, FOCUS_TIMING_CONFIG.FOCUS_RETRY_DELAY)
            } else {
                resolve()
            }
        }, FOCUS_TIMING_CONFIG.INITIAL_FOCUS_DELAY)
    })
}

/**
 * Hook for handling switcher icon navigation with integrated focus preservation
 *
 * @param params - Navigation parameters including article loading state
 * @returns Navigation handler function and focus preservation handlers
 *
 * @example
 * ```typescript
 * const { handleArrowNavigation, handlePreExpansion } = useSwitcherNavigation({
 *     node: currentNode,
 *     treeData: allNodes,
 *     expandedKeys: expandedNodeKeys,
 *     articlesLoading: loadingArticleIds,
 *     treeRef: virtualTreeRef
 * })
 *
 * const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
 *     // Save focus state before expansion
 *     handlePreExpansion(e, node, isExpanded)
 *
 *     // Handle expand/collapse or arrow navigation
 *     if (!handleExpandCollapseKeys(e, node, isExpanded, expandedKeys, handleExpandCollapseArticle)) {
 *         await handleArrowNavigation(e)
 *     }
 * }, [node, isExpanded, expandedKeys, handleArrowNavigation, handlePreExpansion])
 * ```
 */
export const useSwitcherNavigation = ({
    node,
    treeData,
    expandedKeys,
    treeRef,
    articlesLoading,
}: SwitcherNavigationParams) => {
    // Article expansion focus preservation logic
    const { setPendingFocusNode } = useNodeMapActions()
    const { pendingFocusNode } = useNodeMapShallowSelector((state) => ({
        pendingFocusNode: state.pendingFocusNode,
    }))

    /**
     * Refocuses the saved node after loading completes
     */
    const refocusExpandedNode = useCallback(
        (nodeKey: string) => {
            const switcherIcon = document.querySelector(
                getSwitcherIconSelector(nodeKey)
            ) as HTMLElement

            if (
                switcherIcon &&
                !switcherIcon.matches(
                    'div > span > svg[data-testid="switcher-icon"]'
                )
            ) {
                // Only focus if it's not a loading spinner
                switcherIcon.focus()
                // Clear the pending focus node after successful refocus
                setPendingFocusNode(null)
            }
        },
        [setPendingFocusNode]
    )

    /**
     * Effect to detect article loading completion and restore focus
     */
    useEffect(() => {
        if (
            pendingFocusNode &&
            !articlesLoading.includes(pendingFocusNode.nodeKey)
        ) {
            // Loading completed for our saved node - restore focus
            // Small delay to ensure DOM is updated after loading completes
            setTimeout(
                () => refocusExpandedNode(pendingFocusNode.nodeKey),
                FOCUS_TIMING_CONFIG.FOCUS_RESTORATION_DELAY
            )
        }
    }, [articlesLoading, pendingFocusNode, refocusExpandedNode])

    /**
     * Handler to save node information before expansion that will trigger loading
     *
     * @param e - Keyboard event
     * @param targetNode - The node being expanded
     * @param isExpanded - Current expansion state
     */
    const handlePreExpansion = useCallback(
        (
            e: React.KeyboardEvent<HTMLElement>,
            targetNode: DataNode,
            isExpanded: boolean
        ) => {
            // Check if this is an Enter or Space key press
            const enterKey = tryGetShortcutKey('Enter', e, true)
            const spaceKey = tryGetShortcutKey('Space', e, true)

            // If expanding an article that will trigger loading, save node info
            if ((enterKey || spaceKey) && !isExpanded) {
                // Check if this expansion will trigger API loading
                const willTriggerLoading =
                    !targetNode.children?.length ||
                    targetNode.children.length === 0

                if (willTriggerLoading) {
                    // Save node info before expansion for focus restoration
                    setPendingFocusNode({
                        nodeKey: targetNode.key,
                        timestamp: Date.now(),
                    })
                }
            }
        },
        [setPendingFocusNode]
    )
    const handleArrowNavigation = useCallback(
        async (e: React.KeyboardEvent<HTMLElement>): Promise<void> => {
            const arrowUpKey = isArrowUpShortcut(e)
            const arrowDownKey = isArrowDownShortcut(e)

            // Only handle arrow keys
            if (!arrowUpKey && !arrowDownKey) {
                return
            }

            e.preventDefault()
            e.stopPropagation()

            const nextNode = findNextFocusableNode(
                node,
                arrowDownKey ? 'down' : 'up',
                treeData,
                expandedKeys
            )

            if (!nextNode) {
                return
            }

            // Handle virtualized tree focus - scroll first, then focus
            await focusNodeElement(nextNode, treeRef)
        },
        [node, treeData, expandedKeys, treeRef]
    )

    return {
        handleArrowNavigation,
        handlePreExpansion,
        findNextFocusableNode: useCallback(
            (direction: 'up' | 'down') =>
                findNextFocusableNode(node, direction, treeData, expandedKeys),
            [node, treeData, expandedKeys]
        ),
        flattenVisibleNodes: useCallback(
            () => flattenVisibleNodes(treeData, expandedKeys),
            [treeData, expandedKeys]
        ),
    }
}
